/* eslint-disable react-extra/no-inline-styles */
import { ReactNode, useState } from 'react';
import { jsPDF } from 'jspdf';
import { message } from 'antd';
// import { RPConfig, RPDefaultLayout, RPPages, RPProvider } from '@pdf-viewer/react';

import { Button, Image, Modal, PdfUrlViewer, Upload, UploadCertificateIcon } from '~shared/ui';
// import { CompressFile } from '~shared/lib/utils';
import { useTranslation } from '~shared/lib/i18n';
import {
  flutterResultToFile,
  isFlutterWebView,
  openFlutterFileSelector,
} from '~shared/lib/mobile-auth';

import defaultFile from '~shared/assets/defaultFile.svg';
import succesUploadedFile from '~shared/assets/succesUploadedFile.svg';

export interface ModalFileUploaderItem {
  url?: string;
  uploadText?: string;
  setFile?: any;
  onError?: ReactNode;
  label?: string;
  buttonText?: string;
  setUrl?: (a: string) => void;
  icon?: ReactNode;
  bordered?: boolean;
}

export const ModalFileUploader: React.FC<ModalFileUploaderItem> = ({
  url,
  setFile,
  label,
  buttonText,
  setUrl,
  bordered = true,
  icon = <UploadCertificateIcon />,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  const isCurrentUrl = url && url.split('/')[url.split('/').length - 1] !== 'null';

  const handleChange = (e: any) => {
    setLoading(true);
    const file = e.file.originFileObj;

    if (!file) {
      setLoading(false);

      return;
    }

    const fileType = file.type.split('/')[0];

    if (fileType === 'image') {
      const reader = new FileReader();

      reader.onload = (event) => {
        const imageData = event.target?.result;

        if (typeof imageData === 'string') {
          const pdf = new jsPDF();

          pdf.addImage(imageData, 'JPEG', 10, 10, 180, 160);
          const pdfBlob = pdf.output('blob');

          const pdfName = file.name.replace(/\.\w+$/, '') + '.pdf';
          const pdfFile = new File([pdfBlob], pdfName, { type: 'application/pdf' });

          setUrl && setUrl(URL.createObjectURL(pdfFile));
          setFile(pdfFile);
          setUploadedFileName(pdfName);
        }

        setLoading(false);
      };

      reader.onerror = () => setLoading(false);
      reader.readAsDataURL(file);

      return;
    }

    setUrl && setUrl(URL.createObjectURL(file));
    setFile(file);
    setUploadedFileName(file.name);

    setLoading(false);
  };

  const imageToJpegDataUrl = (incoming: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(incoming);
      const img = new window.Image();

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');

          canvas.width = img.naturalWidth || img.width || 1;
          canvas.height = img.naturalHeight || img.height || 1;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('canvas 2d context unavailable'));

            return;
          }

          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          URL.revokeObjectURL(objectUrl);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        } catch (err) {
          URL.revokeObjectURL(objectUrl);
          reject(err);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error(`image decode failed (type=${incoming.type || 'unknown'})`));
      };

      img.src = objectUrl;
    });

  const handleFlutterUpload = () => {
    setLoading(true);

    const dispatched = openFlutterFileSelector(async (result) => {
      if (!result) {
        setLoading(false);

        return;
      }

      let stage = 'start';

      try {
        stage = 'flutterResultToFile';
        const incoming = await flutterResultToFile(result);
        const mimeType = (incoming.type || result.mimeType || '').toLowerCase();
        const fileName = incoming.name || 'document';
        const ext = (fileName.match(/\.([a-z0-9]+)$/i)?.[1] || '').toLowerCase();

        const isPdf = mimeType === 'application/pdf' || ext === 'pdf';
        const imageExts = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'heic', 'heif'];
        const isImage = mimeType.startsWith('image/') || imageExts.includes(ext);

        if (isPdf) {
          const pdfName = fileName.toLowerCase().endsWith('.pdf') ? fileName : `${fileName}.pdf`;
          const pdfFile = new File([incoming], pdfName, { type: 'application/pdf' });

          setUrl && setUrl(URL.createObjectURL(pdfFile));
          setFile(pdfFile);
          setUploadedFileName(pdfName);

          return;
        }

        if (isImage) {
          stage = 'imageToJpeg';
          const jpegDataUrl = await imageToJpegDataUrl(incoming);

          stage = 'jsPDF';
          const pdf = new jsPDF();

          pdf.addImage(jpegDataUrl, 'JPEG', 10, 10, 180, 160);
          const pdfBlob = pdf.output('blob');

          const baseName = fileName.replace(/\.\w+$/, '') || 'document';
          const pdfName = `${baseName}.pdf`;
          const pdfFile = new File([pdfBlob], pdfName, { type: 'application/pdf' });

          setUrl && setUrl(URL.createObjectURL(pdfFile));
          setFile(pdfFile);
          setUploadedFileName(pdfName);

          return;
        }

        message.error(
          `${t('parent:education.fileUploadError', { defaultValue: 'Ошибка загрузки файла!' })} ${t(
            'auth:form.onlyPdf',
            { defaultValue: 'Только PDF или изображение' }
          )}`
        );
      } catch (err) {
        const detail = err instanceof Error ? err.message : String(err);

        // eslint-disable-next-line no-console
        console.error('ModalFileUploader Flutter upload failed', { stage, err });

        message.error(
          `${t('parent:education.fileUploadError', {
            defaultValue: 'Ошибка загрузки файла!',
          })} [${stage}] ${detail}`
        );
      } finally {
        setLoading(false);
      }
    });

    if (!dispatched) {
      setLoading(false);
    }
  };

  const handlePreviewChange = (visible: boolean) => {
    setPreviewVisible(visible);
  };

  return (
    <div
      className={`flex h-auto items-center gap-3 rounded-[16px] bg-white ${
        bordered ? 'p-3 hover:!border-primary' : ''
      }`}
      style={{
        border: bordered ? '1px solid var(--stroke)' : 'none',
      }}
    >
      {/* <div
        className={`w-[60px] h-[60px] bg-bg rounded-full flex items-center justify-center ${
          url ? '[&_path]:stroke-primary' : ''
        }`}
      >
        {icon}
      </div> */}
      <div className="w-[60px] h-[60px] bg-bg rounded-full flex items-center justify-center">
        <Image
          className="w-[60px] h-[60px] rounded-full overflow-hidden"
          fallback={isCurrentUrl ? succesUploadedFile : defaultFile}
          src=""
          preview={
            isCurrentUrl
              ? {
                  visible: false,
                  maskClassName: '!rounded-full',
                  onVisibleChange: handlePreviewChange,
                }
              : false
          }
        />
      </div>

      <div className="flex flex-col gap-2 justify-left">
        <p className="text-[12px] text-left leading-auto">{label}</p>
        {isFlutterWebView() ? (
          <Button
            size="small"
            type="primary"
            ghost
            loading={loading}
            disabled={loading}
            onClick={handleFlutterUpload}
          >
            {buttonText}
          </Button>
        ) : (
          <Upload
            onChange={handleChange}
            itemRender={() => <div />}
            className="flex justify-start"
            disabled={loading}
          >
            <Button size="small" type="primary" ghost loading={loading} disabled={loading}>
              {buttonText}
            </Button>
          </Upload>
        )}
        {uploadedFileName && isCurrentUrl && (
          <button
            type="button"
            onClick={() => handlePreviewChange(true)}
            className="text-[12px] text-primary text-left underline break-all"
          >
            {uploadedFileName}
          </button>
        )}
      </div>

      <Modal
        open={previewVisible}
        onCancel={() => handlePreviewChange(false)}
        footer={null}
        width={1000}
        centered
        // closeIcon={
        //   <div className="rounded-md bg-white min-w-[40px] h-[40px] flex justify-center items-center">
        //     <CloseOutlined />
        //   </div>
        // }
      >
        {/* <iframe
          className="w-full h-[94vh] rounded-[8px]"
          src={url}
          typeof="application/pdf"
          allowFullScreen
          title="Preview"
          onClick={() => handlePreviewChange(false)}
          onLoad={() => handlePreviewChange(true)}
        ></iframe> */}
        <PdfUrlViewer pdfUrl={url || ''} />
      </Modal>
    </div>
  );
};
